import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Divider,
  CircularProgress,
  Alert
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import PerfilAfiliadosListResults from '../../components/PerfilAfiliado/PerfilAfiliadosListResults';
import PerfilAfiliadosListToolbar from '../../components/PerfilAfiliado/PerfilAfiliadosListToolbar';
import affiliateService from '../../services/affiliateService';

const PerfilAfiliadosList = () => {
  const auth = useSelector((state) => state.auth);
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const params = {};
      const response = await affiliateService.getAffiliateProfiles(auth.token, params);

      if (response && response.data) {
        setProfiles(response.data.data || response.data);
      }
    } catch (error) {
      console.error(error);
      swal('Erro', 'Erro ao carregar perfis de afiliados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = React.useCallback(() => {
    let filtered = [...profiles];

    // Filtro de busca
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (profile) => profile.user?.name?.toLowerCase().includes(search)
          || profile.user?.email?.toLowerCase().includes(search)
          || profile.cpf_cnpj?.toLowerCase().includes(search)
      );
    }

    // Filtro de status
    if (statusFilter) {
      filtered = filtered.filter((profile) => profile.status === statusFilter);
    }

    setFilteredProfiles(filtered);
  }, [profiles, searchTerm, statusFilter]);

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleApprove = async (profileId) => {
    try {
      const response = await affiliateService.approveAffiliateProfile(profileId, auth.token);

      if (response && response.data) {
        swal('Sucesso!', 'Perfil aprovado com sucesso', 'success');
        loadProfiles();
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.error || 'Erro ao aprovar perfil';
      swal('Erro', errorMessage, 'error');
    }
  };

  const handleReject = async (profileId, reason) => {
    try {
      const response = await affiliateService.rejectAffiliateProfile(
        profileId,
        reason,
        auth.token
      );

      if (response && response.data) {
        swal('Perfil Rejeitado', 'O afiliado será notificado sobre a rejeição', 'info');
        loadProfiles();
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.error || 'Erro ao rejeitar perfil';
      swal('Erro', errorMessage, 'error');
    }
  };

  const handleViewDetails = (profile) => {
    setSelectedProfile(profile);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedProfile(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Perfis de Afiliados | Sistema de Afiliados</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Perfis de Afiliados
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Gerencie e aprove os cadastros de afiliados
            </Typography>
          </Box>
          <PerfilAfiliadosListToolbar
            onSearchChange={setSearchTerm}
            onStatusChange={setStatusFilter}
            statusFilter={statusFilter}
          />
          <Box sx={{ pt: 3 }}>
            {filteredProfiles.length === 0 ? (
              <Alert severity="info">Nenhum perfil de afiliado encontrado</Alert>
            ) : (
              <PerfilAfiliadosListResults
                profiles={filteredProfiles}
                onApprove={handleApprove}
                onReject={handleReject}
                onViewDetails={handleViewDetails}
              />
            )}
          </Box>
        </Container>
      </Box>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalhes do Perfil de Afiliado
          {selectedProfile && (
            <Box sx={{ mt: 1 }}>
              {selectedProfile.status === 'pending' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Este perfil está aguardando aprovação
                </Alert>
              )}
              {selectedProfile.status === 'approved' && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Perfil aprovado em{' '}
                  {new Date(selectedProfile.approved_at).toLocaleString('pt-BR')}
                </Alert>
              )}
              {selectedProfile.status === 'rejected' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <strong>Motivo da rejeição:</strong> {selectedProfile.rejection_reason}
                </Alert>
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedProfile && (
            <Grid container spacing={3}>
              {/* Dados do Usuário */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Dados do Usuário
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Nome
                </Typography>
                <Typography variant="body1">{selectedProfile.user?.name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  E-mail
                </Typography>
                <Typography variant="body1">{selectedProfile.user?.email || '-'}</Typography>
              </Grid>

              {/* Dados Pessoais */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Dados Pessoais
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  CPF/CNPJ
                </Typography>
                <Typography variant="body1">{selectedProfile.cpf_cnpj || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Telefone
                </Typography>
                <Typography variant="body1">{selectedProfile.phone || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Por que quer ser afiliada?
                </Typography>
                <Typography variant="body1">{selectedProfile.user?.about || '-'}</Typography>
              </Grid>

              {/* Canais de Divulgação */}
              {selectedProfile.user?.channels && selectedProfile.user.channels.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Canais de Divulgação
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>
                              Plataforma
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>
                              Nome do Canal
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', fontWeight: 600 }}>
                              Link
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProfile.user.channels.map((channel) => (
                            <tr key={channel.id}>
                              <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                <Typography variant="body2">
                                  {channel.platform?.charAt(0).toUpperCase() + channel.platform?.slice(1) || '-'}
                                </Typography>
                              </td>
                              <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                  {channel.name || '-'}
                                </Typography>
                              </td>
                              <td style={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                {channel.url ? (
                                  <a
                                    href={channel.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1976d2', textDecoration: 'none', wordBreak: 'break-all' }}
                                  >
                                    {channel.url}
                                  </a>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">-</Typography>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Endereço
                </Typography>
                <Typography variant="body1">{selectedProfile.address || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="body2" color="textSecondary">
                  Cidade
                </Typography>
                <Typography variant="body1">{selectedProfile.city || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Estado
                </Typography>
                <Typography variant="body1">{selectedProfile.state || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  CEP
                </Typography>
                <Typography variant="body1">{selectedProfile.zip_code || '-'}</Typography>
              </Grid>

              {/* Dados Bancários */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Dados Bancários
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Banco
                </Typography>
                <Typography variant="body1">{selectedProfile.bank_name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Código do Banco
                </Typography>
                <Typography variant="body1">{selectedProfile.bank_code || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  Agência
                </Typography>
                <Typography variant="body1">{selectedProfile.agency || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  Conta
                </Typography>
                <Typography variant="body1">{selectedProfile.account_number || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  Tipo de Conta
                </Typography>
                <Typography variant="body1">
                  {selectedProfile.account_type === 'checking' && 'Conta Corrente'}
                  {selectedProfile.account_type === 'savings' && 'Conta Poupança'}
                  {!selectedProfile.account_type && '-'}
                </Typography>
              </Grid>

              {/* Dados PIX */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Dados PIX
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Tipo de Chave
                </Typography>
                <Typography variant="body1">
                  {selectedProfile.pix_type === 'cpf' && 'CPF'}
                  {selectedProfile.pix_type === 'cnpj' && 'CNPJ'}
                  {selectedProfile.pix_type === 'email' && 'E-mail'}
                  {selectedProfile.pix_type === 'phone' && 'Telefone'}
                  {selectedProfile.pix_type === 'random' && 'Chave Aleatória'}
                  {!selectedProfile.pix_type && '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Chave PIX
                </Typography>
                <Typography variant="body1">{selectedProfile.pix_key || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PerfilAfiliadosList;
